import React, { useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { IServiceRecord } from '../../interfaces/statutoryRegister.interface';
import { useToast } from '../../context/ToastContext';
import { EmployeeAutocomplete, EmployeeOption } from '../common/EmployeeAutocomplete';
import { dash, money, sanitizeFilenameSegment, triggerDownload } from './shared';

interface Props {
    institutionId: string;
}

const ServiceRecordTab: React.FC<Props> = ({ institutionId }) => {
    const toast = useToast();
    const [employee, setEmployee] = useState<EmployeeOption | null>(null);
    const [record, setRecord] = useState<IServiceRecord | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const load = async () => {
        if (!employee) return;
        setLoading(true);
        try {
            const data = await StatutoryRegisterService.getServiceRecord(institutionId, employee.id);
            setRecord(data);
        } catch (e) {
            toast.showApiError(e, 'Failed to load service record');
            setRecord(null);
        } finally {
            setLoading(false);
        }
    };

    // H-2: the picker's live selection can move on without a Load — bind the download strictly
    // to the identity of the RECORD ON SCREEN, never to `employee`, so selecting a different
    // employee without pressing Load can never download that other employee's data under the
    // still-displayed employee's filename (or vice versa).
    const recordMatchesSelection = !!record && !!employee && record.userId === employee.id;

    // R19: the filename segment is sanitized client-side to the same whitelist the server applies
    // to Content-Disposition — `a.download` overrides Content-Disposition entirely for a blob URL,
    // so an unsanitized employeeCode here would bypass the server-side filename whitelist.
    const filenameSegment = () => sanitizeFilenameSegment(record?.employeeCode, sanitizeFilenameSegment(employee?.username, 'record'));

    const handleDownload = async () => {
        if (!record || !recordMatchesSelection) return;
        setDownloading(true);
        try {
            const blob = await StatutoryRegisterService.downloadServiceRecordXlsx(institutionId, record.userId);
            triggerDownload(blob, `service-record-${filenameSegment()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!record || !recordMatchesSelection) return;
        setDownloadingPdf(true);
        try {
            const blob = await StatutoryRegisterService.downloadServiceRecordPdf(institutionId, record.userId);
            triggerDownload(blob, `service-record-${filenameSegment()}.pdf`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloadingPdf(false);
        }
    };

    // The payload's own discriminator: the backend sets `message` exactly when figures are
    // withheld, so a status list here would silently drift from the server-side rule.
    const settlementShowsFigures = record?.settlement != null
        && record.settlement.message == null;

    return (
        <Box data-help-page="statutory.serviceRecord.page">
            <Box data-help-section="statutory.serviceRecord.filter" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <Box sx={{ flex: '1 1 280px', maxWidth: 448 }} data-help="statutory.serviceRecord.employee">
                    <EmployeeAutocomplete
                        value={employee}
                        onChange={setEmployee}
                        onError={() => toast.showError('Failed to load employees')}
                    />
                </Box>
                <Button data-help="statutory.serviceRecord.load" variant="outlined" size="small" onClick={load} disabled={!employee || loading}>Load</Button>
                <Button data-help="statutory.serviceRecord.downloadXlsx"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!recordMatchesSelection || downloading}
                    data-testid="service-record-download-button"
                >
                    Download XLSX
                </Button>
                <Button data-help="statutory.serviceRecord.downloadPdf"
                    variant="outlined" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownloadPdf} disabled={!recordMatchesSelection || downloadingPdf}
                    data-testid="service-record-download-pdf-button"
                >
                    Download PDF
                </Button>
                {record && !recordMatchesSelection && (
                    <Typography variant="caption" color="text.secondary" data-testid="service-record-stale-selection-hint">
                        Press Load to refresh before downloading — the selected employee differs from the loaded record.
                    </Typography>
                )}
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && record && (
                <Box data-testid="service-record-content">
                    {record.caveats.map((c, i) => (
                        <Alert key={i} severity="info" sx={{ mb: 1 }} data-help="statutory.serviceRecord.caveat">{c}</Alert>
                    ))}

                    <Paper sx={{ p: 2, mb: 2 }} data-help-section="statutory.serviceRecord.profile">
                        <Typography variant="h6" mb={1}>Profile</Typography>
                        <Typography variant="body2">Name: {record.employeeName ?? '—'}</Typography>
                        <Typography variant="body2">Employee Code: {record.employeeCode ?? '—'}</Typography>
                        <Typography variant="body2">Designation (current): {record.designation ?? '—'}</Typography>
                        <Typography variant="body2">Joining Date: {dash(record.joiningDate)}</Typography>
                        <Typography variant="body2">
                            Current department(s): {record.departments.length ? record.departments.join(', ') : '—'}
                        </Typography>
                    </Paper>

                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" mb={1}>Wage History</Typography>
                        <TableContainer data-help-section="statutory.serviceRecord.wageHistory">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell data-help="statutory.serviceRecord.col.effectiveFrom">Effective From</TableCell>
                                        <TableCell data-help="statutory.serviceRecord.col.current">Current</TableCell>
                                        <TableCell data-help="statutory.serviceRecord.col.component">Component</TableCell>
                                        <TableCell align="right" data-help="statutory.serviceRecord.col.amount">Amount</TableCell>
                                        <TableCell align="right" data-help="statutory.serviceRecord.col.entryTotal">Entry Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {record.wageHistory.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography color="text.secondary">No wage history.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {record.wageHistory.map((entry, ei) => (
                                        entry.components.length === 0 ? (
                                            <TableRow key={ei}>
                                                <TableCell>{entry.effectiveFrom}</TableCell>
                                                <TableCell>{entry.isCurrent ? 'Yes' : 'No'}</TableCell>
                                                <TableCell colSpan={2}>—</TableCell>
                                                <TableCell align="right">{money(entry.total)}</TableCell>
                                            </TableRow>
                                        ) : entry.components.map((comp, ci) => (
                                            <TableRow key={`${ei}-${ci}`}>
                                                <TableCell>{ci === 0 ? entry.effectiveFrom : ''}</TableCell>
                                                <TableCell>{ci === 0 ? (entry.isCurrent ? 'Yes' : 'No') : ''}</TableCell>
                                                <TableCell>{comp.componentName}</TableCell>
                                                <TableCell align="right">{money(comp.amount)}</TableCell>
                                                <TableCell align="right">{ci === 0 ? money(entry.total) : ''}</TableCell>
                                            </TableRow>
                                        ))
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>

                    <Paper sx={{ p: 2, mb: 2 }} data-testid="service-record-exit-block" data-help-section="statutory.serviceRecord.exit">
                        <Typography variant="h6" mb={1}>Exit</Typography>
                        {record.exit ? (
                            <>
                                <Typography variant="body2">Type: {record.exit.exitType}</Typography>
                                <Typography variant="body2">Last Working Day: {dash(record.exit.lastWorkingDay)}</Typography>
                                <Typography variant="body2">Notice Date: {dash(record.exit.noticeDate)}</Typography>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                    </Paper>

                    <Paper sx={{ p: 2, mb: 2 }} data-testid="service-record-settlement-block" data-help-section="statutory.serviceRecord.settlement">
                        <Typography variant="h6" mb={1}>Settlement</Typography>
                        {!record.settlement && (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                        {record.settlement && !settlementShowsFigures && (
                            <>
                                <Chip size="small" label={record.settlement.status} sx={{ mb: 1 }} />
                                <Typography variant="body2" data-testid="settlement-no-figures-message">
                                    {record.settlement.message}
                                </Typography>
                            </>
                        )}
                        {record.settlement && settlementShowsFigures && (
                            <>
                                <Chip size="small" color="success" label={record.settlement.status} sx={{ mb: 1 }} />
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="body2">Total settlement dues: {money(record.settlement.totalAmount)}</Typography>
                                <Typography variant="body2">EL amount: {money(record.settlement.elAmount)}</Typography>
                                <Typography variant="body2">Notice amount: {money(record.settlement.noticeAmount)}</Typography>
                                <Typography variant="body2">Service benefit (paid): {money(record.settlement.serviceBenefitAmount)}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>Gratuity (candidate): {money(record.settlement.gratuityAmount)}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>Statutory compensation (candidate): {money(record.settlement.statutoryCompensationAmount)}</Typography>
                                <Typography variant="body2">Service years: {record.settlement.serviceYears ?? '—'}</Typography>
                                <Typography variant="body2">EL days: {record.settlement.elDays ?? '—'}</Typography>
                            </>
                        )}
                    </Paper>

                    <Paper sx={{ p: 2 }} data-help-section="statutory.serviceRecord.missingFields">
                        <Typography variant="h6" mb={1}>Missing Statutory Fields</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell>{record.missingStatutoryFields.join(', ') || '—'}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}
        </Box>
    );
};

export default ServiceRecordTab;
